//Imports
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

//Hooks
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

//Actions
import { reset, manageSearchUnits } from "./redux/slice";


import {
  clearRedux,
  savePreference,
} from "Redux/actions";
import {
  openModalCreateEditManageCrew,
  openModalEditManageUnitCrew,
} from "../../modals/createEditManageCrew/redux/slice";

//Controls
import {
  Container,
  MenuComponent,
  TableView,
} from "@gpstrackit/gtc-react-controls";
import { HasPermission } from "Core/security";

//Components
import { Grid } from "@mui/material";
import { NotificationManager } from "../../../../../../components/Notifications";
import Preferences from "Core/containers/Preferences";

//Helpers
import { completeColumns } from "Core/data/Helpers";

//Modals
import CreateEditManageCrew from "../../modals/createEditManageCrew";
import DeleteManageCrew from "../../modals/deleteManageCrew";
import { disableItemSelection } from "../../../../.globals/utils";


const UNITS_KEY_PREFERENCE = "Manage-CrewTab-units-list";

const VehicleCrewTab = ({ paginationUnits }) => {
  //States
  const [preferencesUnits, setPreferencesUnits] = useState({});
  const [loadPreferencesUnits, setLoadPreferencesUnits] = useState(false);
  const [loadColumnsUnits, setLoadColumnsUnits] = useState(false);

  //Table refs
  const unitsTableRef = useRef();

  //Preferences refs
  const unitsPreferenceRef = useRef();

  //Language
  const { messages, locale } = useIntl();

  // Permissions
  const user = useSelector((state) => state.securityRedux.user);
  const editablePermissions = HasPermission(user, "", [337])

  //Redux

  const openCreateEditManageCrew = useSelector(
    (state) => state.manageCreateEditManageCrewRedux.open
  );
  const openDeleteManageCrew = useSelector(
    (state) => state.manageDeleteManageCrewRedux.open
  );

  const  { unitsList, loadingUnits, error , totalUnits} = useSelector((state) => state.manageUnitsRedux);

  const authenticatedId = useSelector(
    (state) => state.securityRedux.authenticatedId
  );
  const dispatch = useDispatch();

 

  if (error) {
    NotificationManager.error(
      messages["errorMessage"],
      messages["error"],
      3000,
      null,
      null,
      "warning"
    );
    dispatch(reset({ error: null }));
  };


  const getUnitsMenuOptions = (crews) => [
    crews?.length
      ? messages["manage_crew_units_editCrew"]
      : messages["manage_crew_units_assignCrew"],
  ];


  const handleClickUnitMenu = (type, unit) => {
    if (type === messages["manage_crew_units_editCrew"]) {
      dispatch(openModalEditManageUnitCrew(unit));
      return;
    }
    if (type === messages["manage_crew_units_assignCrew"]) {
      dispatch(openModalCreateEditManageCrew({ unit, isAssignToUnit: true }));
      return;
    }
  };


  const savePreferencesUnits = useCallback(
    (preferencesArray) => {
      if (!Array.isArray(preferencesArray)) {
        console.error("Expected preferences to be an array");
        return;
      }

      let updatedPreferences = { ...preferencesUnits } || {};

      preferencesArray.forEach(({ key, value }) => {
        updatedPreferences[key] = value;
      });

      setPreferencesUnits(updatedPreferences);

      dispatch(
        savePreference({
          entityName: "User",
          entityId: authenticatedId,
          preferenceKey: UNITS_KEY_PREFERENCE,
          preferenceValue: updatedPreferences,
        })
      );
    },
    [preferencesUnits, authenticatedId]
  );

  const ensureVisibleColumns = useCallback((columns, fallbackKey) => {
    if (!Array.isArray(columns) || !columns.length) return columns;

    const hasVisibleColumns = columns.some((column) => column?.show !== false);
    if (hasVisibleColumns) return columns;

    const hasFallbackColumn = columns.some((column) => column?.key === fallbackKey);

    return columns.map((column, index) => ({
      ...column,
      show: hasFallbackColumn
        ? column?.key === fallbackKey
        : index === 0,
    }));
  }, []);


  const currentUnitsColumns = useMemo(
    () => [
      {
        key: "label",
        title: messages["manage_crew_units_label"],
        functionString: 'return row?.label || ""',
        show: true,
        export: true,
        exportValue: (row) => row?.label,
        render: (row) => (
          <Grid container alignItems="center">
            <Grid item>
              <p>{row?.label}</p>
            </Grid>
            <Grid item>
              {editablePermissions &&
                <MenuComponent
                  placement="right-start"
                  options={getUnitsMenuOptions(row?.crews)}
                  icon="more_vert"
                  className="showInHover"
                  onClick={handleClickUnitMenu}
                  value={row}
                />
              }
            </Grid>
          </Grid>
        ),
      },
      {
        key: "crews",
        title: messages["manage_crew_units_crewAssigned"],
        functionString:
          'return row?.crews?.map((crew, index) => `${crew.firstName} ${crew.lastName}`).join(", ")',
        show: true,
        export: true,
        exportValue: (row) =>
          row?.crews
            ?.map((crew, index) => `${crew.firstName} ${crew.lastName}`)
            .join(", "),
        render: (row) => {


         return row?.crews?.length ? (
            <Grid container alignItems="center">
              <Grid item>
                <p>
                  {row?.crews
                    ?.map((crew, index) => `${crew.firstName} ${crew.lastName}`)
                    .join(", ")}
                </p>
              </Grid>
            </Grid>
          ) : (
            <Fragment>
              <a
                className="link"
                onClick={() =>
                  dispatch(
                    openModalCreateEditManageCrew({
                      unit: row,
                      isAssignToUnit: true,
                    })
                  )
                }
              >
                {messages["assign"]}
              </a>
            </Fragment>
          )
        }
      },
    ],
    [locale]
  );

  useEffect(() => {
    if (loadingUnits) return;
    paginationUnits.onChangeData(unitsList || []);
  }, []);

  useEffect(() => {
    paginationUnits.setColumns(currentUnitsColumns);
    unitsTableRef?.current?.setPage(1);
    unitsTableRef?.current?.setSelectedItems([]);
  }, [currentUnitsColumns]);

  useEffect(() => {

    const unitsPreferences = unitsPreferenceRef?.current?.getPreferences(
      "User",
      UNITS_KEY_PREFERENCE
    );
    setPreferencesUnits(unitsPreferences);
    setLoadPreferencesUnits(false);
  }, [ unitsPreferenceRef]);


  useEffect(() => {
    if (preferencesUnits?.columns?.length && !loadColumnsUnits) {
      const preferenceMap = new Map(
        preferencesUnits.columns.map((col) => [col.key, col])
      );

      // Reorder columns based on preferences (keeping `show`)
      const reorderedColumns = preferencesUnits.columns
        .map((pref) => {
          const col = currentUnitsColumns.find((c) => c.key === pref.key);
          return col ? { ...col, show: pref.show } : null;
        })
        .filter(Boolean);

      // Add new columns (not listed in preferences), with `show: true` by default
      const newColumns = currentUnitsColumns
        .filter((col) => !preferenceMap.has(col.key))
        .map((col) => ({ ...col, show: true }));

      const finalColumns = [...reorderedColumns, ...newColumns];

      paginationUnits.setColumns(finalColumns);
      setPreferencesUnits({ ...preferencesUnits });
      setLoadColumnsUnits(true);
    }
  }, [paginationUnits.columns, loadColumnsUnits, preferencesUnits]);

  useEffect(() => {
    if (paginationUnits.columns.length > 0) {
      setPreferencesUnits((currentPreferences) => ({
        ...currentPreferences,
        columns: paginationUnits.columns,
      }));
      setLoadColumnsUnits(false);
    }
  }, [locale]);

  return (
    <Fragment>
      <Container maxWidth={false}>
          <Preferences
            ref={unitsPreferenceRef}
            preferencesKey={UNITS_KEY_PREFERENCE}
          >
            <TableView
                keyColumn="id"
                loading={loadingUnits || loadPreferencesUnits}
                topheight={235}
                disableItemSelection={disableItemSelection(preferencesUnits)}
                data={unitsList}
                total={totalUnits}
                exportOrientation="landscape"
                onSelectedItems={(items) => setSelectedItems(items)}
                {...paginationUnits}

                onChangeColumns={(columns) => {
                    const currentColumns = paginationUnits?.columns || [];
                    const finalsColumns = completeColumns(currentColumns, columns);
                  const safeColumns = ensureVisibleColumns(finalsColumns, "label");
                    savePreferencesUnits([
                  { key: "columns", value: safeColumns },
                    ]);
                  paginationUnits.onChangeColumns(safeColumns);
                }}
            />
          </Preferences>
      </Container>
    </Fragment>
  );
};

export default VehicleCrewTab;
